import { COURSE_INFO, GROUP_ROLE_ACCESS } from '@/constant/QueryKeyName';
import useToggle from '@/hooks/useToggle';
import useUserPlan from '@/hooks/useUserPlan';
import { AxiosError } from 'axios';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useQueryClient } from 'react-query';
import { UrlObject } from 'url';
import { checkIsUserJoined } from '../../../common/checkRoleUser';
import { ROLE_ACCESS, USER_PLAN } from '../../../constant/storage';
import useRequestAccessGroup from '../../../hooks/my-group/useRequestAccessGroup';
import useToast from '../../../hooks/useToast';
import {
  FolderStatusEnum,
  ICourseDetailResponse,
} from '../../../interfaces/training.interface';
import { QuickLoginModalHOC } from '../../modal/LoginModal2HOC';

const DynamicModalJoinGroup = dynamic(() => import('../course/ModalJoinGroup'));
const DynamicModalVerifyAccount = dynamic(
  () => import('../course/ModalVerifyAccount')
);

const RequiredJoinGroupHOC: React.FC<{
  href: UrlObject | string | null;
  isRequiresVerified?: boolean;
  className?: string;
  disabled?: boolean;
  onAfterJoiningGroup?: () => void;
}> = ({
  children,
  href,
  isRequiresVerified,
  className,
  disabled,
  onAfterJoiningGroup = () => {},
}) => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const { cid } = router.query;
  const toast = useToast();
  const userPlan = useUserPlan();

  const courseDetailQueryReturn: ICourseDetailResponse | undefined =
    queryClient.getQueryData([COURSE_INFO, cid as string]);
  const groupId = courseDetailQueryReturn?.group?.id;

  const mineRole: ROLE_ACCESS | undefined = groupId
    ? queryClient.getQueryData([GROUP_ROLE_ACCESS, groupId])
    : ROLE_ACCESS.Viewer;

  const isPrivateGroup =
    courseDetailQueryReturn?.group?.status === FolderStatusEnum.Private;

  const [openModalVerify, toggleModalVerify] = useToggle();
  const [openModalJoinGroup, toggleModalJoinGroup] = useToggle();

  const needVerifiedAccount =
    (userPlan === USER_PLAN.Education || userPlan === USER_PLAN.Personal) &&
    isRequiresVerified &&
    checkIsUserJoined(mineRole) === true;

  const onClickIfUserIsAMember = () => {
    onAfterJoiningGroup();
    if (href) {
      router.push(href);
    }
  };

  const {
    mutationReturn: { mutate, isLoading },
  } = useRequestAccessGroup({
    isRequested: mineRole === ROLE_ACCESS.Requested,
    opt: {
      async onSuccess() {
        await queryClient.refetchQueries([GROUP_ROLE_ACCESS, groupId]);
        openModalJoinGroup && toggleModalJoinGroup();

        const _mineRole: ROLE_ACCESS | undefined = queryClient.getQueryData([
          GROUP_ROLE_ACCESS,
          groupId,
        ]);

        if (
          (userPlan === USER_PLAN.Education ||
            userPlan === USER_PLAN.Personal) &&
          isRequiresVerified &&
          checkIsUserJoined(_mineRole) === true
        ) {
          toggleModalVerify();
          return;
        }

        const groupName = courseDetailQueryReturn?.group?.name;

        if (isPrivateGroup) {
          toast(
            `Bạn đã ${
              mineRole === ROLE_ACCESS.Requested ? 'hủy' : 'gửi'
            } yêu cầu tham gia nhóm ${groupName}`,
            'success'
          );
          return;
        } else {
          toast(`Tham gia nhóm ${groupName}.`, 'success');
        }

        const userHasPaid = courseDetailQueryReturn?.userOrder?.hasUserPaid;
        const isPaidCourse = courseDetailQueryReturn?.coursePrice?.isPaidCourse;

        if (isPaidCourse && !userHasPaid) return;

        onClickIfUserIsAMember();
      },
      onError(_err) {
        const err = _err as AxiosError;
        if (err.response?.status !== 400) {
          toast('Có lỗi xảy ra.', 'error');
          return;
        } // return 400 when user have already join group
        onClickIfUserIsAMember();
      },
    },
  });

  const handleClickJoinGroup = () => {
    if (!groupId) return;
    mutate({
      groupId: groupId,
      groupStatus:
        courseDetailQueryReturn?.group?.status || FolderStatusEnum.Public,
    });
  };

  return (
    <>
      <QuickLoginModalHOC
        disabled={disabled}
        classname={className}
        handleClickWhenDisabledQuickLogin={() => {
          if (isLoading) return;

          if (!checkIsUserJoined(mineRole) && isPrivateGroup) {
            handleClickJoinGroup();
            return;
          }

          if (!checkIsUserJoined(mineRole)) {
            toggleModalJoinGroup();
            return;
          }

          if (needVerifiedAccount) {
            toggleModalVerify();
            return;
          }
          onClickIfUserIsAMember();
        }}
        loginSuccessCb={() => window.location.reload()}
      >
        {children}
      </QuickLoginModalHOC>

      {openModalJoinGroup && (
        <DynamicModalJoinGroup
          open={openModalJoinGroup}
          onClose={toggleModalJoinGroup}
          onClickJoinGroup={handleClickJoinGroup}
        />
      )}
      {openModalVerify && (
        <DynamicModalVerifyAccount
          open={openModalVerify}
          onClose={toggleModalVerify}
        />
      )}
    </>
  );
};

export default RequiredJoinGroupHOC;
